<script setup lang="ts">
import { onStartTyping, useFocus } from '@vueuse/core'
import { vMaska } from 'maska/vue'
import { nextTick, watch } from 'vue'
import { useToggle } from '@vueuse/core'
import { useTemplateRef } from '#imports'

const searchTerm = defineModel<string>()
const focusSearch = defineModel<boolean>('focus')

const props = defineProps<{
  id?: string
  ariaLabel?: string
  error?: boolean
  loading?: boolean
  placeholder: string
  initialCollapsed?: boolean
  collapsible?: boolean
  maskFormat?: string
}>()

defineEmits<{
  enter: [input: string]
}>()

const inputRef = useTemplateRef('inputRef')
const { focused } = useFocus(inputRef)

watch(
  focusSearch,
  async (val: boolean | undefined) => {
    if (val) {
      await nextTick()
      ;(document.activeElement as HTMLElement)?.blur()
      focused.value = true
    }
  },
  {
    immediate: true,
  },
)
const clear = () => {
  searchTerm.value = ''
  if (props.collapsible) {
    toggleCollapse()
    focused.value = false
  }
}

const [collapsed, toggleCollapse] = useToggle(props.initialCollapsed ?? true)

onStartTyping(() => {
  if (inputRef.value === document.activeElement) {
    return
  }
  collapsed.value = false
  focused.value = true
})
</script>

<template>
  <div
    :class="[
      'border-px font-display relative flex h-[36px] items-center justify-between rounded-lg border-neutral-200 text-neutral-500 transition-all',
      {
        'border-error-dark': error,
        'w-full': !collapsible,
        'min-w-[36px] max-w-[36px]': collapsed,
        'focus-within:border-secondary-600 max-w-full': !collapsed,
      },
    ]"
  >
    <component
      :is="collapsible ? 'button' : 'div'"
      v-on="{
        click() {
          if (collapsible) {
            toggleCollapse()
            if (!collapsed) {
              focused = true
            }
          }
        },
      }"
      :class="[
        'z-2 absolute flex size-8 items-center justify-center rounded-lg',
        {
          absolute: !collapsed,
          'pointer-events-none': !collapsible,
          'text-error-dark': error,
          'text-neutral-500': !error,
        },
      ]"
      :aria-controls="collapsible ? id : undefined"
      :aria-expanded="collapsible ? !collapsed : undefined"
    >
      <span class="i-ph-magnifying-glass-bold inline-block size-4" />
    </component>

    <input
      :id="id"
      ref="inputRef"
      v-model="searchTerm"
      v-maska="maskFormat"
      :class="[
        'z-1 w-full transform-gpu rounded-lg text-xs leading-4 transition-all focus:outline-none',
        {
          'bg-error-light border-error-dark text-error-dark placeholder:text-error-300':
            error,
          'border-neutral-300 bg-transparent text-neutral-900 placeholder:text-neutral-400':
            !error,
          'pr-8': searchTerm?.length,
          'max-w-0 opacity-0': collapsed,
          'max-w-full p-2 pl-8': !collapsed,
        },
      ]"
      :aria-invalid="error"
      :aria-label="ariaLabel || 'Search'"
      :disabled="collapsed"
      :placeholder="placeholder"
      :tabindex="collapsed ? -1 : 0"
      type="search"
      :data-maska-eager="true"
      @keyup.esc="clear()"
    />
    <div
      class="pointer-events-none absolute flex h-full w-full transform flex-col justify-end overflow-hidden rounded-lg bg-white"
    >
      <transition
        enter-active-class="transform"
        enter-from-class="translate-y-1"
        enter-to-class="translate-y-0"
        leave-active-class="transform"
        leave-from-class="translate-y-0"
        leave-to-class="translate-y-1"
      >
        <div
          v-if="loading"
          class="h-3px bg-primary-light-hover -z-1 w-full animate-pulse transition-transform"
        >
          <div class="bar-value bg-primary-light h-full w-full rounded" />
        </div>
      </transition>
    </div>
    <button
      v-if="searchTerm && !collapsed"
      :class="[
        'btn-icon outline-primary-300 z-2 absolute right-0 flex size-8 transform items-center justify-center rounded-lg hover:text-neutral-700 focus:text-neutral-700',
        {
          'text-error-dark': error,
        },
      ]"
      type="button"
      @click="clear()"
    >
      <div v-if="searchTerm" class="i-ph-x-bold w-4" />
    </button>
  </div>
</template>

<style scoped>
/* Hide the default search cancel button */
input[type='search']::-webkit-search-cancel-button {
  -webkit-appearance: none;
  display: none;
  opacity: 0;
  pointer-events: none;
}

.bar-value {
  animation: indeterminateAnimation 1s infinite linear;
  transform-origin: 0% 50%;
}

@keyframes indeterminateAnimation {
  0% {
    transform: translateX(0) scaleX(0);
  }
  24% {
    transform: translateX(0) scaleX(0.24);
  }
  62% {
    transform: translateX(38%) scaleX(0.62);
  }
  100% {
    transform: translateX(100%) scaleX(0.62);
  }
}
</style>
